import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import {
  Archive,
  CircleDollarSign,
  CircleDot,
  LetterText,
  PlusCircle,
  ReceiptText,
  Star,
  Type,
} from "lucide-react";
import { useState } from "react";
import { formatCurrency } from "shared";
import { toast } from "sonner";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import type { FiltersState } from "@/components/data-table-filter/core/types";
import { IconButton } from "@/components/icon-button";
import { Page } from "@/components/page";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import type { ColumnDefWithMeta } from "@/components/table/dataTable.types";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { cn } from "@/lib/clientUtils";
import type { RouterOutputs } from "@/lib/trpcClient";

type PolicyType = RouterOutputs["expensePolicy"]["list"][number];
const dtf = createColumnConfigHelper<PolicyType>();

const columnsConfig = [
  dtf
    .text()
    .id("Name")
    .accessor((row) => row.name)
    .displayName("Name")
    .icon(Type)
    .build(),
  dtf
    .option()
    .id("Status")
    .accessor((row) => row.status)
    .displayName("Status")
    .icon(CircleDot)
    .transformOptionFn((u) => {
      return {
        label: u,
        value: u,
      };
    })
    .hide()
    .build(),
  dtf
    .option()
    .id("Budget Required")
    .accessor((row) => row.requireBudget)
    .displayName("Budget Required Status")
    .icon(CircleDollarSign)
    .transformOptionFn((u) => {
      return {
        label: u ? "Required" : "Not Required",
        value: String(u),
      };
    })
    .build(),
  dtf
    .number()
    .id("Receipt Required Amount")
    .accessor((row) => (row.receiptCents ?? 0) / 100)
    .displayName("Receipt Required Amount")
    .icon(ReceiptText)
    .build(),
  dtf
    .number()
    .id("Memo Required Amount")
    .accessor((row) => (row.memoCents ?? 0) / 100)
    .displayName("Memo Required Amount")
    .icon(LetterText)
    .build(),
];

const initialFilterState: FiltersState = [
  {
    columnId: "Status",
    hide: true,
    operator: "is",
    type: "option",
    values: ["active"],
  },
];

export const Route = createFileRoute("/_authed/_org/policies/expense")({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.expensePolicy.list.queryOptions());
  },
});

type View = "active" | "archived";

function getInitialViewFromFilters(filters: FiltersState): View {
  const budgetStatus = filters.find((filter) => filter.columnId === "Status");
  if (!budgetStatus) return "active";

  return budgetStatus.values[0] as View;
}

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const location = useLocation();
  const navigate = Route.useNavigate();
  const { admin } = useRequiredRole();

  const { data: policies, isLoading } = useQuery(
    trpc.expensePolicy.list.queryOptions(),
  );

  const { mutate: makeDefaultPolicy } = useMutation(
    trpc.expensePolicy.makeDefault.mutationOptions({
      onError: (error) => {
        toast.error("Failed to set default policy", {
          description: error.message,
        });
      },
      onSuccess: () => {
        toast.success("Default policy updated");
      },
    }),
  );

  const { mutate: archivePolicy } = useMutation(
    trpc.expensePolicy.archive.mutationOptions({
      onError: (error) => {
        toast.error("Failed to archive policy", {
          description: error.message,
        });
      },
      onSuccess: () => {
        toast.success("Policy archived");
      },
    }),
  );

  const { mutate: unarchivePolicy } = useMutation(
    trpc.expensePolicy.unArchive.mutationOptions({
      onError: (error) => {
        toast.error("Failed to unarchive policy", {
          description: error.message,
        });
      },
      onSuccess: () => {
        toast.success("Policy restored");
        queryClient.invalidateQueries(trpc.expensePolicy.list.queryOptions());
      },
    }),
  );

  // Define columns for the DataTable
  const tableColumns: ColumnDefWithMeta<PolicyType>[] = [
    {
      accessorFn: (row) => row.name,
      accessorKey: "Name",
      cell: ({ row }) => {
        const isDefault = row.original.isDefault;

        return (
          <div className="group flex items-center justify-between">
            <div className="flex flex-col">
              <span className="font-medium">{row.original.name}</span>
              <span className="font-light text-xs">
                {row.original.description}
              </span>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={cn(
                    "ml-2 rounded-sm p-1 transition-colors duration-200",
                    isDefault ? "cursor-default" : "hover:bg-muted",
                  )}
                  disabled={isDefault}
                  onClick={() => {
                    if (!isDefault) {
                      makeDefaultPolicy({ policyId: row.original.id });
                    }
                  }}
                  type="button"
                >
                  <Star
                    className={cn(
                      "h-4 w-4 transition-all duration-200",
                      isDefault
                        ? "fill-yellow-400 text-yellow-400" // Visible yellow star when default
                        : "text-transparent group-hover:text-gray-400", // Hidden until hover, gray when hovered
                    )}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {isDefault ? "Default policy" : "Make default"}
              </TooltipContent>
            </Tooltip>
          </div>
        );
      },
      enableGlobalFilter: true,
      id: "Name",
      meta: {
        type: "string",
      },
    },
    {
      accessorFn: (row) => String(row.requireBudget),
      accessorKey: "Budget Required",
      cell: ({ row }) => (
        <div>{row.original.requireBudget ? "Required" : "Not required"}</div>
      ),
      id: "Budget Required",
      meta: {
        type: "string",
      },
    },
    {
      accessorFn: (row) => row.receiptCents,
      accessorKey: "Receipt Required",
      cell: ({ row }) => {
        return (
          <div>
            {row.original.receiptCents === null
              ? "Not required"
              : row.original.receiptCents === 0
                ? "Required for all transactions"
                : `Required over ${formatCurrency(row.original.receiptCents)}`}
          </div>
        );
      },
      id: "Receipt Required",
      meta: {
        type: "string",
      },
    },
    {
      accessorFn: (row) => row.memoCents,
      accessorKey: "Memo Required",
      cell: ({ row }) => {
        return (
          <div>
            {row.original.memoCents === null
              ? "Not required"
              : row.original.memoCents === 0
                ? "Required for all transactions"
                : `Required over ${formatCurrency(row.original.memoCents)}`}
          </div>
        );
      },
      id: "Memo Required",
      meta: {
        type: "string",
      },
    },
    {
      accessorKey: "Actions",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end gap-2">
            <Link
              params={{
                id: row.original.id,
              }}
              to="/policies/expense/$id/edit"
            >
              <IconButton icon="pencil" tooltip="Edit Policy" />
            </Link>

            <IconButton
              icon={
                row.original.status === "archived"
                  ? "archive-restore"
                  : "archive"
              }
              tooltip={
                row.original.status === "archived"
                  ? "Unarchive Policy"
                  : "Archive Policy"
              }
              onClick={
                row.original.status === "archived"
                  ? () => unarchivePolicy({ policyId: row.original.id })
                  : () => archivePolicy({ policyId: row.original.id })
              }
            />
          </div>
        );
      },
      enableSorting: false,
      id: "Actions",
      meta: {
        align: "right",
        type: "string",
      },
    },
    {
      accessorFn: (row) => row.status,
      id: "Status",
      meta: {
        type: "string",
      },
    },
  ];
  const tableName = "policies";
  const { table, filters, actions, columns, strategy, setTableFilters } =
    usePersistedReactTable({
      columns: tableColumns,
      data: policies ?? [],
      filterOptions: {
        columnsConfig,
        defaultFilters: initialFilterState,
        strategy: "client",
      },
      getRowId: (row) => row.id.toString(),
      initialState: {
        columnVisibility: {
          Status: false,
        },
      },
      tableName: tableName,
    });

  const [view, setView] = useState<View>(getInitialViewFromFilters(filters));

  const updateStatusFilter = (status: View) => {
    setView(status);
    setTableFilters((filters) =>
      filters.map((f) =>
        f.columnId === "Status"
          ? {
              ...f,
              values: [status],
            }
          : f,
      ),
    );
  };

  const activePoliciesCount = policies?.filter(
    (policy) => policy.status === "active",
  ).length;

  const archivedPoliciesCount = policies?.filter(
    (policy) => policy.status === "archived",
  ).length;

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/policies/expense",
          });
        }
      }}
      open={
        location.pathname !== "/policies/expense/" &&
        location.pathname.includes("/policies/expense/")
      }
    >
      <Page
        heading="Expense Requirements"
        actions={
          admin && (
            <Link to="/policies/expense/create">
              <Button>
                <PlusCircle />
                Create Policy
              </Button>
            </Link>
          )
        }
        tabs={
          <Tabs
            onValueChange={(v) => {
              updateStatusFilter(v as View);
            }}
            value={view}
          >
            <TabsList variant="standard">
              <TabsTrigger
                value="active"
                variant="standard"
                count={activePoliciesCount}
              >
                <span className="flex items-center gap-2">
                  <CircleDot className="size-3.5 text-neutral-400" />
                  Active
                </span>
              </TabsTrigger>

              <TabsTrigger
                value="archived"
                variant="standard"
                count={archivedPoliciesCount}
              >
                <span className="flex items-center gap-2">
                  <Archive className="size-3.5 text-neutral-400" />
                  Archived
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
        table={{
          actions,
          columns,
          excludeColumnIds: ["Status"],
          filters,
          strategy,
          table,
        }}
      >
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">No Matching Policies</span>

              <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                Clear Filters
              </Button>
            </div>
          }
          table={table}
          tableName={tableName}
          loading={isLoading}
          className="[&_th]:border-t-0!"
        />

        <TablePagination tableName={tableName} table={table} />
      </Page>

      <SheetContent
        className="w-full"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <Outlet />
      </SheetContent>
    </Sheet>
  );
}
